import dayjs from 'utils/dayjs';
import ObjectUtils from '@spothero/utils/object';
import {spotheroGet, spotheroPatch} from 'api';
import {formatPrice} from 'utils/number-format';
import {displayDateFormat} from 'utils/date-time';
import {sendSentryException} from 'utils/error-handling';

/** @module api/rates/rate-thresholds/dto */
/** @module api/rates/rate-thresholds */

/**
 * @deprecated
 * @function formatFacilityRateThresholdsResponse
 * @param {TRateThresholdResponse[]} data - RateThresholds collection
 * @return {TRateThreshold[]}
 *
 * @memberof module:api/rates/rate-thresholds/dto
 */
export const formatFacilityRateThresholdsResponse = (data) => {
    const formattedData = ObjectUtils.camelizeKeys(data);

    const response = formattedData.map((rateThreshold) => ({
        ...rateThreshold,
        modifiedAt: rateThreshold.threshold
            ? dayjs(rateThreshold.threshold.modifiedAt).format(
                  displayDateFormat
              )
            : null,
        threshold: rateThreshold.threshold
            ? formatPrice(rateThreshold.threshold.threshold)
            : null,
        id: rateThreshold.policy.policy,
    }));

    return response;
};

/**
 * @deprecated
 * @function formatFacilityRateThresholdsRequest
 * @param {TRateThreshold[]} data - RateThresholds collection
 * @return {TRateThresholdRequest[]}
 *
 * @memberof module:api/rates/rate-thresholds/dto
 */
export const formatFacilityRateThresholdsRequest = (data) => {
    // filter out any rate thresholds that aren't set
    const filteredRateThresholds = data.filter(({threshold}) => threshold);

    // format for the API
    const request = filteredRateThresholds.map((rateThreshold) => ({
        policy: rateThreshold.policy.policy,
        threshold: formatPrice(rateThreshold.threshold, {
            formatterCallback: (value) => Number(value.toFixed()),
            toCents: true,
        }),
    }));

    return request;
};

/**
 * Gets the rate thresholds for a facility
 * @deprecated
 * @function
 *
 * @param {data} data - The data to send to the API
 * @param {number} data.facilityId - The facility id
 * @return {Promise<TRateThreshold[]>}
 * @throws {Error}
 *
 * @see '/api/v1/facilities/{facilityId}/rates/rate-thresholds/'
 *
 * Todo:  Remove this endpoint entirely, once SIQ is sunsetted tentatively 6/30/2026
 */
export const getFacilityRateThresholds = async ({facilityId}) => {
    try {
        const response = await spotheroGet(
            `/facilities/${facilityId}/rates/rate-thresholds/`
        );

        const formattedResponse = formatFacilityRateThresholdsResponse(
            response.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Updates the rate rateThresholds for a facility
 * @deprecated
 * @param {data} data - The data to send to the API
 * @param {number} data.facilityId - The facility id
 * @param {TRateThreshold[]} data.rateThresholds - The rateThresholds to update
 * @return {Promise<TRateThreshold[]>}
 * @throws {Error}
 *
 * @see '/api/v1/facilities/{facilityId}/rates/rate-thresholds/'
 */
export const updateFacilityRateThresholds = async ({
    facilityId,
    rateThresholds,
}) => {
    try {
        const formattedRequest =
            formatFacilityRateThresholdsRequest(rateThresholds);

        const response = await spotheroPatch(
            `/facilities/${facilityId}/rates/rate-thresholds/`,
            formattedRequest
        );

        const formattedResponse = formatFacilityRateThresholdsResponse(
            response.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets all the available rate threshold policy definitions
 * @deprecated
 * @return {Promise<TRateThresholdPolicy[]>}
 * @throws {Error}
 *
 * @see '/api/v1/facilities/rates/rate-threshold-definitions/'
 */
export const getFacilityRateThresholdPolicyDefinitions = async () => {
    try {
        const response = await spotheroGet(
            `/facilities/rates/rate-threshold-definitions/`
        );

        const formattedResponse = ObjectUtils.camelizeKeys(
            response.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
